import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';

import LogoLockup from '../../chambers/LogoLockup';
import ContactContent, { CONTACT_HEADING } from '../../legal/ContactContent';
import LegalClause from '../../legal/LegalClause';
import PrivacyContent from '../../legal/PrivacyContent';
import RefundPolicyContent, { REFUND_POLICY_HEADING } from '../../legal/RefundPolicyContent';
import ShippingPolicyContent, { SHIPPING_POLICY_HEADING } from '../../legal/ShippingPolicyContent';
import TermsContent from '../../legal/TermsContent';
import {
  TRUST_CENTER,
  TRUST_DOCS,
  TRUST_SECTION,
  trustCenterSections,
  trustDocFor,
  trustTopics,
  type TrustDocKey,
} from './homepageContent';

/**
 * The Trust Center as a full-page view that slides in over the homepage (Ajay, 2026-11-03):
 * its tabs are the Trust Center itself, Terms, Privacy, Cancellation & Refund, Shipping &
 * Delivery and Contact Us. Nothing on the homepage leaves it for these: a click on any link to
 * one of them — header, footer, the Trust strip, the Trust Center's own list — slides this view
 * in at that tab. Closing it (Back to home, ✕, Esc, or the browser's Back) slides it away and
 * leaves the homepage exactly where it was, with focus back on the link that opened it.
 * A modified click (new tab or window) still opens the standalone page.
 *
 * The open view writes its tab to the URL hash (/#privacy), so a document can be linked to;
 * opening from a link adds one history entry, switching tabs inside replaces it.
 *
 * It is a modal dialog: the homepage behind is `inert` and does not scroll, focus stays inside.
 * The documents are the components the standalone pages render, so the two never differ. They
 * are written in the app's theme tokens; the sheet pins those tokens to its parchment, because
 * the landing never follows the app's dark mode.
 */

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2';

/** The slide-out takes 300 ms (`duration-300`); the page stays inert a little longer. */
const CLOSE_MS = 350;

function setInert(el: HTMLElement | null, on: boolean) {
  if (!el) return;
  if (on) el.setAttribute('inert', '');
  else el.removeAttribute('inert');
}

function DocHeading({ overline, title }: { overline: string; title: string }) {
  return (
    <>
      <p className="text-xs tracking-[0.14em] uppercase text-legal-green-700 mb-2">{overline}</p>
      <h3 className="text-3xl font-semibold text-text-primary mb-8">{title}</h3>
    </>
  );
}

function TrustCenterDoc() {
  return (
    <>
      <p className="mb-10 text-[15px] leading-relaxed text-text-secondary">{TRUST_CENTER.intro}</p>
      {trustCenterSections().map((section, i) => (
        <LegalClause key={section.id} id={section.id} n={String(i + 1)} title={section.title}>
          {section.paragraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
          {section.links && (
            <ul className="list-disc pl-5 space-y-1.5">
              {section.links.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="underline font-medium text-text-primary">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </LegalClause>
      ))}
    </>
  );
}

function TrustDoc({ doc, open }: { doc: TrustDocKey; open: (key: TrustDocKey) => void }) {
  switch (doc) {
    case 'trust':
      return <TrustCenterDoc />;
    case 'terms':
      return <TermsContent onOpenPrivacy={() => open('privacy')} onOpenRefund={() => open('refund-policy')} />;
    case 'privacy':
      return <PrivacyContent onOpenTerms={() => open('terms')} />;
    case 'refund-policy':
      return (
        <>
          <DocHeading {...REFUND_POLICY_HEADING} />
          <RefundPolicyContent onOpenTerms={() => open('terms')} onOpenContact={() => open('contact')} />
        </>
      );
    case 'shipping-policy':
      return (
        <>
          <DocHeading {...SHIPPING_POLICY_HEADING} />
          <ShippingPolicyContent onOpenRefund={() => open('refund-policy')} />
        </>
      );
    case 'contact':
      return (
        <>
          <DocHeading {...CONTACT_HEADING} />
          <ContactContent />
        </>
      );
  }
}

interface TrustCenterSlideProps {
  /** The homepage behind the view: made inert while the view is open. */
  pageRef: RefObject<HTMLElement>;
}

export default function TrustCenterSlide({ pageRef }: TrustCenterSlideProps) {
  // Arriving on a link to a document (/#privacy) opens the view at it.
  const [open, setOpen] = useState(() => trustDocFor(window.location.hash) !== null);
  const [active, setActive] = useState<TrustDocKey>(() => trustDocFor(window.location.hash) ?? 'trust');
  const dialogRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // The link that opened the view, to hand focus back to — or, when that link has gone (the
  // mobile menu closes itself on a click), the button of the menu it was in. Whether opening
  // added a history entry (then closing goes back over it, so Back and ✕ agree). Where the page
  // stood, so it is exactly there again after closing.
  const returnFocus = useRef<HTMLElement | null>(null);
  const fallbackFocus = useRef<HTMLElement | null>(null);
  const pushed = useRef(false);
  const openedAtY = useRef<number | null>(null);

  // A tab chosen inside the view replaces the hash; it never adds history.
  const switchTo = useCallback((key: TrustDocKey) => {
    setActive(key);
    window.history.replaceState(window.history.state, '', `#${key}`);
  }, []);

  // Following a link inside a document (a cross-reference, or the Trust Center's own list of
  // policies) replaces the document it was in, so focus moves to the new document instead of
  // being dropped with the link.
  const follow = useCallback(
    (key: TrustDocKey) => {
      switchTo(key);
      panelRef.current?.focus({ preventScroll: true });
    },
    [switchTo],
  );

  const close = useCallback(() => {
    setOpen(false);
    if (pushed.current) {
      pushed.current = false;
      window.history.back();
    } else {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target instanceof Element ? e.target.closest<HTMLElement>('a[href]') : null;
      const key = trustDocFor(link?.getAttribute('href'));
      if (!key) return;
      e.preventDefault();
      if (dialogRef.current?.contains(link)) {
        follow(key); // the Trust Center's own list of policies
        return;
      }
      returnFocus.current = link;
      const menu = link?.closest('[id]');
      fallbackFocus.current = menu
        ? document.querySelector<HTMLElement>(`[aria-controls="${menu.id}"]`)
        : null;
      // Reading the position makes the browser finish the layout of a menu that has just
      // closed (it shifts the page) BEFORE pushState saves the position with the entry —
      // otherwise Back would restore the stale one.
      openedAtY.current = window.scrollY;
      if (window.location.hash !== `#${key}`) {
        window.history.pushState(null, '', `#${key}`);
        pushed.current = true;
      }
      setActive(key);
      setOpen(true);
    };
    // Back and forward, or a hash typed into the address bar.
    const onHashChange = () => {
      const key = trustDocFor(window.location.hash);
      if (key) {
        setActive(key);
        setOpen(true);
      } else {
        pushed.current = false;
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    window.addEventListener('hashchange', onHashChange);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [follow]);

  // Open: the homepage goes inert and stops scrolling underneath; focus moves into the view.
  // Closed: the view goes inert at once, but it is still sliding out for 300 ms — the page
  // stays inert until it has gone, so a double-click on ✕ or Back to home lands on nothing
  // instead of the header under it (Start Free Trial). Then focus returns to the opener and
  // the page is where it was. A timer, not transitionend: reduced motion cuts the slide to 0.
  useEffect(() => {
    if (!open) {
      setInert(dialogRef.current, true);
      const timer = window.setTimeout(() => {
        setInert(pageRef.current, false);
        const opener = returnFocus.current?.isConnected ? returnFocus.current : fallbackFocus.current;
        opener?.focus({ preventScroll: true });
        if (openedAtY.current !== null && Math.abs(window.scrollY - openedAtY.current) > 1) {
          window.scrollTo(0, openedAtY.current);
        }
        returnFocus.current = null;
        fallbackFocus.current = null;
        openedAtY.current = null;
      }, CLOSE_MS);
      return () => window.clearTimeout(timer);
    }
    setInert(pageRef.current, true);
    setInert(dialogRef.current, false);
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = 'hidden';
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, pageRef]);

  // Each document starts at its top, with its tab in view in the (scrolling) tab row.
  useEffect(() => {
    if (!open) return;
    if (dialogRef.current) dialogRef.current.scrollTop = 0;
    tabRefs.current[TRUST_DOCS.findIndex((d) => d.key === active)]?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [active, open]);

  // Focus stays inside the view while it is open.
  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]'),
    ).filter((el) => el.tabIndex >= 0);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Arrow keys move along the tabs (wrapping), Home/End jump to the ends; selection follows
  // focus, as in the hero.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = TRUST_DOCS.length - 1;
    const next =
      e.key === 'ArrowRight' ? (index === lastIndex ? 0 : index + 1)
      : e.key === 'ArrowLeft' ? (index === 0 ? lastIndex : index - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? lastIndex
      : -1;
    if (next < 0) return;
    e.preventDefault();
    switchTo(TRUST_DOCS[next].key);
    tabRefs.current[next]?.focus();
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={TRUST_SECTION.heading}
      aria-hidden={open ? undefined : true}
      onKeyDown={onDialogKeyDown}
      // Opening shows the view at once and slides it in; closing slides it out, then hides it.
      className={`fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-chamber-rail font-instrument duration-300 ease-out ${
        open ? 'visible translate-x-0 transition-transform' : 'invisible translate-x-full transition-[transform,visibility]'
      }`}
    >
      <div className="sticky top-0 z-10 border-b border-brass-hairline bg-chamber-rail/95 backdrop-blur-sm">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-5 sm:px-8 lg:px-14 py-2.5">
          <button
            type="button"
            onClick={close}
            className={`justify-self-start flex items-center gap-2 text-[13px] font-medium tracking-[0.04em] text-chamber-muted hover:text-brass transition-colors cursor-pointer ${FOCUS}`}
          >
            <span aria-hidden="true">←</span>
            {TRUST_SECTION.back}
          </button>
          <LogoLockup
            markClass="h-[22px] sm:h-[26px] w-auto text-brass"
            textClass="font-display text-[18px] sm:text-[21px] leading-none tracking-[0.01em] text-parchment"
          />
          <button
            type="button"
            onClick={close}
            aria-label={`Close the ${TRUST_SECTION.heading}`}
            className={`justify-self-end flex h-9 w-9 items-center justify-center border border-chamber-border-hi text-parchment hover:border-brass hover:text-brass transition-colors cursor-pointer ${FOCUS}`}
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </button>
        </div>
        <div
          role="tablist"
          aria-label="Trust Center and policies"
          className="flex gap-2 overflow-x-auto px-5 sm:px-8 lg:px-14 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {TRUST_DOCS.map((d, i) => {
            const selected = d.key === active;
            return (
              <button
                key={d.key}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`trust-tab-${d.key}`}
                aria-selected={selected}
                aria-controls="trust-doc"
                tabIndex={selected ? 0 : -1}
                onClick={() => switchTo(d.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
                className={`shrink-0 whitespace-nowrap border px-4 py-2 text-[12px] font-semibold tracking-[0.08em] uppercase transition-colors cursor-pointer ${FOCUS} ${
                  selected
                    ? 'border-brass bg-brass text-chamber-ink'
                    : 'border-chamber-border-hi text-chamber-muted hover:border-brass hover:text-brass'
                }`}
              >
                {d.tab}
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={panelRef}
        role="tabpanel"
        id="trust-doc"
        aria-labelledby={`trust-tab-${active}`}
        tabIndex={0}
        className={`px-5 sm:px-8 lg:px-14 pt-10 sm:pt-14 pb-20 ${FOCUS}`}
      >
        {/* One centred column for every tab, so the Trust Center's statements line up with
            the document under them. */}
        <div className="mx-auto max-w-[960px]">
          {active === 'trust' && (
            <>
              <h2 className="font-display font-normal text-3xl lg:text-[44px] text-parchment">{TRUST_SECTION.heading}</h2>
              <p className="text-base leading-[1.7] text-chamber-muted-bright max-w-[600px] mt-4">{TRUST_SECTION.intro}</p>
              {/* Per-cell borders, not a gap-px background: the number of statements moves with
                  the gates, and a short last row must leave plain page, not a filled block. */}
              <dl className="mt-10 mb-10 grid border-l border-t border-brass-hairline-faint sm:grid-cols-2">
                {trustTopics().map((t) => (
                  <div key={t.topic} className="border-r border-b border-brass-hairline-faint p-6">
                    <dt className="font-plex text-[10.5px] font-medium tracking-[0.2em] uppercase text-brass">{t.topic}</dt>
                    <dd className="mt-2 font-editorial text-[16px] leading-[1.5] text-parchment">{t.statement}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          {/* The document sheet. The bracketed classes pin the app's theme tokens, which the
              documents are written in, to parchment values. */}
          <div className="border border-brass-hairline bg-parchment-card px-6 py-8 sm:px-10 sm:py-12 text-chamber-ink [--text-primary:#2C2C2C] [--text-secondary:#5C6353] [--border-primary:#E4DCC2] [--surface-subtle:#EFE7D0]">
            <div className="mx-auto max-w-[820px]">
              <TrustDoc doc={active} open={follow} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
